import { yupResolver } from "@hookform/resolvers/yup";
import { Badge, Button, Col, Form, Row } from "react-bootstrap";
import { useFieldArray, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { DropdownFilter, showToast, WizardFooter } from "src/componentes";
import { ConsultaMunicipioModule } from "src/hook/Consulta/ConsultaMunicipio.Module";
import { MunicipioDto } from "src/pages/Privada/Entidade/Municipio/Municipio.Dto";
import {
  getLocal,
  MDFeInput,
  STORAGEMDFE_KEY,
  updateLocal,
  validarMunicipioPertenceUf,
} from "../MDFe.Dto";
import {
  CarregamentoFormType,
  CarregamentoSchema,
} from "./MDFe.Cadastro.Dto";

const MDFeCadastroCarregamentoView = () => {
  const navigate = useNavigate();
  const consultaMunicipio = ConsultaMunicipioModule();
  const mdfe = getLocal<MDFeInput>(STORAGEMDFE_KEY);
  const ufInicio = mdfe?.ufInicio;

  const form = useForm<CarregamentoFormType>({
    resolver: yupResolver(CarregamentoSchema),
    defaultValues: {
      municipiosCarregamento:
        mdfe?.municipiosCarregamento && mdfe.municipiosCarregamento.length > 0
          ? mdfe.municipiosCarregamento
          : [
              {
                codigoMunicipioCarregamento: 0,
                nomeMunicipioCarregamento: "",
              },
            ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "municipiosCarregamento",
  });

  const handleVoltar = () => {
    navigate("../veiculo");
  };

  const onSubmit = (data: CarregamentoFormType) => {
    if (ufInicio) {
      let divergente = false;
      data.municipiosCarregamento.forEach((m, idx) => {
        const cod = Number(m.codigoMunicipioCarregamento);
        if (cod && !validarMunicipioPertenceUf(cod, ufInicio)) {
          divergente = true;
          form.setError(`municipiosCarregamento.${idx}.codigoMunicipioCarregamento`, {
            type: "manual",
            message: `Código de Município (${cod}) diverge da UF de carregamento do MDF-e (${ufInicio}) (Rejeição 456).`,
          });
        }
      });
      if (divergente) {
        showToast({
          message: `Um ou mais municípios divergem da UF de carregamento do MDF-e (${ufInicio}) (Rejeição 456). Verifique os municípios selecionados.`,
          title: "UF de Carregamento Divergente",
          variant: "Danger",
        });
        return;
      }
    }

    updateLocal<MDFeInput>(STORAGEMDFE_KEY, {
      municipiosCarregamento: data.municipiosCarregamento.map((m) => ({
        codigoMunicipioCarregamento: Number(m.codigoMunicipioCarregamento),
        nomeMunicipioCarregamento: m.nomeMunicipioCarregamento,
      })),
    });

    navigate("../documentos");
  };

  const onError = () => {
    showToast({
      message:
        "O grupo 'Municípios de Carregamento' é obrigatório. Informe ao menos um município de carregamento válido.",
      title: "Grupo Obrigatório",
      variant: "Warning",
    });
  };

  return (
    <Form noValidate onSubmit={form.handleSubmit(onSubmit, onError)} className="mt-3 mb-5">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5 className="text-primary mb-0">Municípios de Carregamento</h5>
          <div className="d-flex align-items-center gap-2 mt-1">
            <Badge bg="info" text="dark">
              UF de Início (Carregamento): {ufInicio || "Não informada"}
            </Badge>
          </div>
          <small className="text-muted">
            Informe onde as mercadorias foram ou serão carregadas no veículo.
          </small>
        </div>
        <Button
          variant="outline-primary"
          size="sm"
          onClick={() =>
            append({
              codigoMunicipioCarregamento: 0,
              nomeMunicipioCarregamento: "",
            })
          }
        >
          + Adicionar Município
        </Button>
      </div>

      {fields.map((field, index) => (
        <div key={field.id} className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="fw-semibold text-secondary">
              Local de Carregamento #{index + 1}
            </span>
            {fields.length > 1 && (
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => remove(index)}
              >
                Remover
              </Button>
            )}
          </div>
          <Row className="g-3">
            <Col xs={12}>
              <DropdownFilter<MunicipioDto>
                fetchData={consultaMunicipio.MunicipioBuscar}
                loading={consultaMunicipio.loading}
                labelDescription="Município de Carregamento (digite o nome da cidade)"
                getDescription={(item: MunicipioDto) =>
                  `${item.nomeMunicipio} - ${item.ufMunicipio} (IBGE: ${item.codigoMunicipio})`
                }
                getId={(item: MunicipioDto) => item.codigoMunicipio}
                registeredFields={{
                  id: form.register(
                    `municipiosCarregamento.${index}.codigoMunicipioCarregamento`
                  ),
                  description: form.register(
                    `municipiosCarregamento.${index}.nomeMunicipioCarregamento`
                  ),
                }}
                errorsMessageDescription={
                  form.formState.errors.municipiosCarregamento?.[index]
                    ?.codigoMunicipioCarregamento
                }
                onSelect={(item: MunicipioDto | undefined) => {
                  if (item) {
                    if (
                      ufInicio &&
                      !validarMunicipioPertenceUf(item.codigoMunicipio, ufInicio)
                    ) {
                      showToast({
                        message: `O município selecionado (${item.nomeMunicipio} - ${item.ufMunicipio}) diverge da UF de carregamento do MDF-e (${ufInicio}) (Rejeição 456).`,
                        title: "Município Divergente (Rejeição 456)",
                        variant: "Warning",
                      });
                    }
                    form.setValue(
                      `municipiosCarregamento.${index}.codigoMunicipioCarregamento`,
                      Number(item.codigoMunicipio),
                      { shouldValidate: true }
                    );
                    form.setValue(
                      `municipiosCarregamento.${index}.nomeMunicipioCarregamento`,
                      item.nomeMunicipio,
                      { shouldValidate: true }
                    );
                  } else {
                    form.setValue(
                      `municipiosCarregamento.${index}.codigoMunicipioCarregamento`,
                      0,
                      { shouldValidate: true }
                    );
                    form.setValue(
                      `municipiosCarregamento.${index}.nomeMunicipioCarregamento`,
                      "",
                      { shouldValidate: true }
                    );
                  }
                }}
              />
            </Col>
          </Row>
        </div>
      ))}

      <WizardFooter onVoltar={handleVoltar} />
    </Form>
  );
};

export default MDFeCadastroCarregamentoView;
